import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    ExclamationTriangleIcon,
    CheckCircleIcon,
    ClockIcon,
    ArrowPathIcon,
    ArrowRightIcon,
    PlusIcon,
    MagnifyingGlassIcon,
    XMarkIcon,
    PhotoIcon,
    MapPinIcon,
    UserCircleIcon,
    BellAlertIcon,
    ArrowUpCircleIcon,
    UserIcon,
    PhoneIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { apiGet, apiPost, apiPatch } from '../utils/api';

// ─── Types ───────────────────────────────────────────────────────────────────
type IssueCategory = 'social' | 'financial' | 'operational' | 'infrastructure' | 'agronomy';
type IssueUrgency = 'low' | 'medium' | 'high' | 'critical';
type IssueStatus = 'submitted' | 'assigned' | 'in_progress' | 'resolved' | 'closed';

interface Issue {
    id: number;
    title?: string;
    description: string;
    category: IssueCategory;
    urgency: IssueUrgency;
    status: IssueStatus;
    source_status?: string;
    reported_by: string;
    reported_by_role?: string | null;
    farmer_id?: number;
    farmer_name?: string;
    farmer_phone?: string;
    tree_id?: number;
    tree_code?: string;
    crop_type?: string;
    variety?: string;
    tree_health_status?: string;
    assigned_to?: string;
    cohort_name?: string;
    location?: string;
    address?: string;
    created_at: string;
    updated_at: string;
    resolution_notes?: string;
    photo_url?: string;
    photo_urls?: string[];
    audio_url?: string;
    report_metadata?: Record<string, unknown>;
    latitude?: number;
    longitude?: number;
}

interface TreeOption {
    id: number;
    tree_code: string;
    crop_type?: string;
    variety?: string;
    farmer_id?: number;
    farmer_name?: string;
    cohort_name?: string;
    health_status?: string;
    field_plot_id?: number;
}

// ─── Config ───────────────────────────────────────────────────────────────────
const CATEGORIES: Record<IssueCategory, { label: string; color: string; bg: string; icon: string }> = {
    social: { label: 'Social', color: 'text-pink-700', bg: 'bg-pink-100', icon: '👥' },
    financial: { label: 'Financial', color: 'text-yellow-700', bg: 'bg-yellow-100', icon: '💰' },
    operational: { label: 'Operational', color: 'text-blue-700', bg: 'bg-blue-100', icon: '⚙️' },
    infrastructure: { label: 'Infrastructure', color: 'text-gray-700', bg: 'bg-gray-100', icon: '🏗️' },
    agronomy: { label: 'Agronomy', color: 'text-green-700', bg: 'bg-green-100', icon: '🌱' },
};

const URGENCY: Record<IssueUrgency, { label: string; color: string; bg: string; dot: string }> = {
    low: { label: 'Low', color: 'text-green-700', bg: 'bg-green-50 border-green-200', dot: 'bg-green-500' },
    medium: { label: 'Medium', color: 'text-yellow-700', bg: 'bg-yellow-50 border-yellow-200', dot: 'bg-yellow-500' },
    high: { label: 'High', color: 'text-orange-700', bg: 'bg-orange-50 border-orange-200', dot: 'bg-orange-500' },
    critical: { label: 'Critical', color: 'text-red-700', bg: 'bg-red-50 border-red-200', dot: 'bg-red-600' },
};

const STATUS_FLOW: Record<IssueStatus, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
    submitted: { label: 'Submitted', color: 'text-blue-700', bg: 'bg-blue-100', icon: <ArrowUpCircleIcon className="w-4 h-4" /> },
    assigned: { label: 'Assigned', color: 'text-purple-700', bg: 'bg-purple-100', icon: <UserCircleIcon className="w-4 h-4" /> },
    in_progress: { label: 'In Progress', color: 'text-yellow-700', bg: 'bg-yellow-100', icon: <ClockIcon className="w-4 h-4" /> },
    resolved: { label: 'Resolved', color: 'text-green-700', bg: 'bg-green-100', icon: <CheckCircleIcon className="w-4 h-4" /> },
    closed: { label: 'Closed', color: 'text-gray-600', bg: 'bg-gray-100', icon: <XMarkIcon className="w-4 h-4" /> },
};

const normalizeStatus = (status: any): IssueStatus => {
    const value = String(status || 'submitted').toLowerCase().replace(/\s+/g, '_');
    if (value === 'open') return 'submitted';
    if (value === 'pending') return 'submitted';
    if (['submitted', 'assigned', 'in_progress', 'resolved', 'closed'].includes(value)) return value as IssueStatus;
    return 'submitted';
};

const normalizeCategory = (category: any): IssueCategory => {
    const value = String(category || '').toLowerCase();
    if (value.includes('tree') || value.includes('pest') || value.includes('disease') || value.includes('crop') || value.includes('field issue') || value.includes('input quality') || value.includes('water') || value.includes('weather')) return 'agronomy';
    if (value.includes('social') || value.includes('land dispute')) return 'social';
    if (value.includes('financial') || value.includes('vsla') || value.includes('payment')) return 'financial';
    if (value.includes('equipment') || value.includes('warehouse')) return 'infrastructure';
    if (['social', 'financial', 'operational', 'infrastructure', 'agronomy'].includes(value)) return value as IssueCategory;
    return 'operational';
};

const normalizeUrgency = (urgency: any): IssueUrgency => {
    const value = String(urgency || 'medium').toLowerCase();
    if (['low', 'medium', 'high', 'critical'].includes(value)) return value as IssueUrgency;
    return 'medium';
};

const normalizeIssue = (issue: any): Issue => ({
    ...issue,
    source_status: String(issue.status || 'submitted').toLowerCase(),
    status: normalizeStatus(issue.status),
    category: normalizeCategory(issue.category),
    urgency: normalizeUrgency(issue.urgency),
    location: issue.location || issue.address,
    title: issue.title || (issue.tree_code ? `Tree issue - ${issue.tree_code}` : issue.title),
    latitude: issue.latitude ? Number(issue.latitude) : undefined,
    longitude: issue.longitude ? Number(issue.longitude) : undefined,
    photo_urls: issue.photo_urls || (issue.photo_url ? [issue.photo_url] : []),
});

// ─── Sub-components ──────────────────────────────────────────────────────────
const Badge: React.FC<{ label: string; color: string; bg: string; dot?: string; icon?: React.ReactNode; size?: 'sm' | 'md' }> = ({ label, color, bg, dot, icon, size = 'sm' }) => (
    <span className={`inline-flex items-center gap-1 ${size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs'} font-semibold rounded-full ${bg} ${color} whitespace-nowrap`}>
        {dot && <span className={`w-1.5 h-1.5 rounded-full ${dot} flex-shrink-0`} />}
        {icon && <span className="flex-shrink-0">{icon}</span>}
        {label}
    </span>
);

const StatCard: React.FC<{ title: string; value: number | string; subtitle?: string; icon: React.ReactNode; accent: string; trend?: 'up' | 'down' }> = ({ title, value, subtitle, icon, accent }) => (
    <div className="bg-white rounded-lg border border-slate-200 p-4 flex items-center gap-3">
        <div className={`p-2.5 rounded-md ${accent} flex-shrink-0`}>{icon}</div>
        <div className="min-w-0">
            <p className="text-xs text-slate-500 font-medium truncate">{title}</p>
            <p className="text-xl font-bold text-slate-900">{value}</p>
            {subtitle && <p className="text-[11px] text-slate-400 truncate">{subtitle}</p>}
        </div>
    </div>
);

// ─── Report Issue Modal ───────────────────────────────────────────────────────
interface ReportModalProps { onClose: () => void; onSubmit: (data: any) => Promise<void>; trees: TreeOption[]; }
const ReportIssueModal: React.FC<ReportModalProps> = ({ onClose, onSubmit, trees }) => {
    const [step, setStep] = useState(1);
    const [fields, setFields] = useState<any[]>([]);
    const [loadingFields, setLoadingFields] = useState(false);
    const [selectedField, setSelectedField] = useState<any>(null);
    const [issueScope, setIssueScope] = useState<'field' | 'tree'>('field');
    const [selectedTree, setSelectedTree] = useState<any>(null);
    const [form, setForm] = useState({
        title: '', description: '', category: '' as string,
        urgency: 'medium' as IssueUrgency,
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    useEffect(() => {
        setLoadingFields(true);
        apiGet<any>('/api/maps/farm-plots')
            .then(res => {
                const list = res?.plots || res?.data || (Array.isArray(res) ? res : []);
                setFields(list);
            })
            .catch(() => setFields([]))
            .finally(() => setLoadingFields(false));
    }, []);

    const fieldTrees = selectedField
        ? trees.filter(t => Number(t.field_plot_id) === Number(selectedField.id))
        : [];

    const CATEGORIES_GRID = [
        { value: 'Pest/Disease Outbreak', icon: '🐛', label: 'Pest/Disease', color: 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100' },
        { value: 'Tree Health Issue', icon: '🌳', label: 'Tree Health', color: 'bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100' },
        { value: 'Field Issue', icon: '🌾', label: 'Field Issue', color: 'bg-yellow-50 border-yellow-200 text-yellow-700 hover:bg-yellow-100' },
        { value: 'Water Shortage', icon: '💧', label: 'Water', color: 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100' },
        { value: 'Weather Damage', icon: '⛈️', label: 'Weather', color: 'bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100' },
        { value: 'Equipment Failure', icon: '🔧', label: 'Equipment', color: 'bg-teal-50 border-teal-200 text-teal-700 hover:bg-teal-100' },
        { value: 'Input Quality Problem', icon: '📦', label: 'Input Quality', color: 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100' },
        { value: 'Training Material Missing', icon: '📚', label: 'Training', color: 'bg-cyan-50 border-cyan-200 text-cyan-700 hover:bg-cyan-100' },
        { value: 'VSLA Payment Delay', icon: '💰', label: 'VSLA/Payment', color: 'bg-pink-50 border-pink-200 text-pink-700 hover:bg-pink-100' },
        { value: 'Land Dispute', icon: '🗺️', label: 'Land Dispute', color: 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100' },
        { value: 'Other', icon: '📋', label: 'Other', color: 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100' },
    ];

    const URGENCY_OPTIONS = [
        { value: 'low', icon: '⬇️', label: 'Low', desc: 'Next weekly cycle', color: 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100' },
        { value: 'medium', icon: '➡️', label: 'Medium', desc: 'Within 3 days', color: 'bg-yellow-50 border-yellow-200 text-yellow-700 hover:bg-yellow-100' },
        { value: 'high', icon: '⬆️', label: 'High', desc: 'Within 24 hours', color: 'bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100' },
        { value: 'critical', icon: '🔴', label: 'Critical', desc: 'Immediate alert', color: 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100' },
    ];

    const canNext = () => {
        if (step === 1) return selectedField !== null;
        if (step === 2) return true;
        if (step === 3) return form.category !== '';
        if (step === 4) return true;
        if (step === 5) return form.title.trim().length > 0 && form.description.trim().length > 0;
        return true;
    };

    const handleSubmit = async () => {
        if (!form.title.trim() || !form.description.trim() || !form.category) return;
        setSubmitting(true);
        try {
            await onSubmit({
                title: form.title,
                description: form.description,
                category: form.category,
                urgency: form.urgency,
                field_plot_id: selectedField?.id,
                tree_id: selectedTree?.id,
                farmer_id: selectedTree?.farmer_id || selectedField?.farmer_id,
                address: selectedField?.name || selectedField?.plot_code || '',
                status: 'Open',
            });
            onClose();
        } catch (error: any) {
            setSubmitError(error?.message || 'The issue could not be submitted. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const stepLabels = ['Location', 'Scope', 'Category', 'Urgency', 'Details'];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="bg-gradient-to-r from-green-600 to-emerald-500 px-6 py-4 flex items-center justify-between flex-shrink-0">
                    <div className="text-white">
                        <h2 className="text-lg font-bold">Report an Issue</h2>
                        <p className="text-green-100 text-sm">Step {step} of 5 — {stepLabels[step - 1]}</p>
                    </div>
                    <button onClick={onClose} className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors">
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                {/* Progress Bar */}
                <div className="px-6 pt-4 flex-shrink-0">
                    <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map(s => (
                            <div key={s} className={`h-1 flex-1 rounded-full transition-colors ${s <= step ? 'bg-green-500' : 'bg-gray-200'}`} />
                        ))}
                    </div>
                    <div className="flex justify-between mt-2">
                        {stepLabels.map((label, i) => (
                            <span key={i} className={`text-[10px] font-medium ${i + 1 <= step ? 'text-green-600' : 'text-gray-400'}`}>{label}</span>
                        ))}
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto px-6 py-5">
                    {/* Step 1: Select Field */}
                    {step === 1 && (
                        <div className="space-y-4">
                            <div>
                                <h3 className="text-base font-bold text-gray-900 mb-1">Select Field</h3>
                                <p className="text-sm text-gray-500">Choose the field where the issue was observed</p>
                            </div>
                            {loadingFields ? (
                                <div className="flex items-center justify-center py-8 text-gray-400">
                                    <ArrowPathIcon className="w-5 h-5 animate-spin mr-2" /> Loading fields...
                                </div>
                            ) : fields.length === 0 ? (
                                <div className="text-center py-8 text-gray-400">No fields available</div>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[40vh] overflow-y-auto">
                                    {fields.map((f: any) => (
                                        <button
                                            key={f.id}
                                            onClick={() => { setSelectedField(f); setStep(2); }}
                                            className={`text-left p-3 rounded-xl border-2 transition-all ${
                                                selectedField?.id === f.id
                                                    ? 'border-green-500 bg-green-50'
                                                    : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
                                            }`}
                                        >
                                            <p className="font-semibold text-sm text-gray-900 truncate">{f.name || f.plot_code || `Field #${f.id}`}</p>
                                            <p className="text-xs text-gray-500 mt-0.5">{f.crop_type || '—'} {f.area_hectares ? `• ${f.area_hectares} ha` : ''}</p>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Step 2: Scope */}
                    {step === 2 && (
                        <div className="space-y-4">
                            <div>
                                <h3 className="text-base font-bold text-gray-900 mb-1">Issue Scope</h3>
                                <p className="text-sm text-gray-500">Is this a field-wide or tree-specific issue?</p>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    onClick={() => { setIssueScope('field'); setSelectedTree(null); }}
                                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                                        issueScope === 'field'
                                            ? 'border-green-500 bg-green-50'
                                            : 'border-gray-200 hover:border-green-300'
                                    }`}
                                >
                                    <span className="text-2xl">🌾</span>
                                    <p className="font-bold text-sm text-gray-900 mt-2">Whole Field</p>
                                    <p className="text-xs text-gray-500 mt-0.5">Issue affects the entire field</p>
                                </button>
                                <button
                                    onClick={() => setIssueScope('tree')}
                                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                                        issueScope === 'tree'
                                            ? 'border-green-500 bg-green-50'
                                            : 'border-gray-200 hover:border-green-300'
                                    }`}
                                >
                                    <span className="text-2xl">🌳</span>
                                    <p className="font-bold text-sm text-gray-900 mt-2">Specific Tree</p>
                                    <p className="text-xs text-gray-500 mt-0.5">Issue on a particular tree</p>
                                </button>
                            </div>
                            {issueScope === 'tree' && (
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Select Tree</label>
                                    <select
                                        value={selectedTree?.id || ''}
                                        onChange={e => {
                                            const tree = fieldTrees.find(t => Number(t.id) === Number(e.target.value));
                                            setSelectedTree(tree || null);
                                        }}
                                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                                    >
                                        <option value="">Choose a tree...</option>
                                        {fieldTrees.map(t => (
                                            <option key={t.id} value={t.id}>{t.tree_code} {t.farmer_name ? `- ${t.farmer_name}` : ''}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Step 3: Category */}
                    {step === 3 && (
                        <div className="space-y-4">
                            <div>
                                <h3 className="text-base font-bold text-gray-900 mb-1">Issue Category</h3>
                                <p className="text-sm text-gray-500">What type of issue is this?</p>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                {CATEGORIES_GRID.map(cat => (
                                    <button
                                        key={cat.value}
                                        onClick={() => setForm(f => ({ ...f, category: cat.value }))}
                                        className={`p-3 rounded-xl border-2 text-left transition-all ${
                                            form.category === cat.value
                                                ? 'border-green-500 bg-green-50 ring-2 ring-green-200'
                                                : cat.color
                                        }`}
                                    >
                                        <span className="text-xl">{cat.icon}</span>
                                        <p className="text-xs font-semibold mt-1">{cat.label}</p>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Step 4: Urgency */}
                    {step === 4 && (
                        <div className="space-y-4">
                            <div>
                                <h3 className="text-base font-bold text-gray-900 mb-1">Urgency Level</h3>
                                <p className="text-sm text-gray-500">How urgent is this issue?</p>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                {URGENCY_OPTIONS.map(u => (
                                    <button
                                        key={u.value}
                                        onClick={() => setForm(f => ({ ...f, urgency: u.value as IssueUrgency }))}
                                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                                            form.urgency === u.value
                                                ? 'border-green-500 bg-green-50 ring-2 ring-green-200'
                                                : u.color
                                        }`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className="text-xl">{u.icon}</span>
                                            <div>
                                                <p className="font-bold text-sm">{u.label}</p>
                                                <p className="text-xs opacity-70">{u.desc}</p>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Step 5: Details */}
                    {step === 5 && (
                        <div className="space-y-4">
                            <div>
                                <h3 className="text-base font-bold text-gray-900 mb-1">Issue Details</h3>
                                <p className="text-sm text-gray-500">Provide a title and detailed description</p>
                            </div>

                            {/* Summary */}
                            <div className="bg-gray-50 rounded-xl p-3 flex flex-wrap gap-2 text-xs">
                                <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">{selectedField?.name || 'Field'}</span>
                                {selectedTree && <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium">{selectedTree.tree_code}</span>}
                                <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-medium">{form.category}</span>
                                <span className={`px-2 py-1 rounded-full font-medium ${form.urgency === 'critical' ? 'bg-red-100 text-red-700' : form.urgency === 'high' ? 'bg-orange-100 text-orange-700' : 'bg-yellow-100 text-yellow-700'}`}>{form.urgency}</span>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Issue Title <span className="text-red-500">*</span>
                                </label>
                                <input
                                    value={form.title}
                                    onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                                    placeholder="Brief, descriptive title"
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Description <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    value={form.description}
                                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                                    rows={4}
                                    placeholder="Describe the issue in detail. Include context, who is affected, and any steps already taken..."
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400 resize-none"
                                />
                            </div>

                            <p className="text-xs text-gray-500">For photo or voice evidence, submit this report from the mobile field app.</p>
                            {submitError && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{submitError}</p>}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between flex-shrink-0">
                    <button
                        onClick={() => step > 1 ? setStep(s => s - 1) : onClose()}
                        className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        {step > 1 ? '← Back' : 'Cancel'}
                    </button>
                    <div className="flex gap-2">
                        {step < 5 ? (
                            <button
                                onClick={() => setStep(s => s + 1)}
                                disabled={!canNext()}
                                className="px-5 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold shadow hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                            >
                                Continue →
                            </button>
                        ) : (
                            <button
                                onClick={handleSubmit}
                                disabled={submitting || !form.title.trim() || !form.description.trim()}
                                className="px-5 py-2 bg-gradient-to-r from-green-600 to-emerald-500 text-white rounded-lg text-sm font-semibold shadow hover:shadow-md disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                            >
                                {submitting ? <><ArrowPathIcon className="w-4 h-4 animate-spin" /> Submitting...</> : 'Submit Report'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── Issue Detail Drawer ──────────────────────────────────────────────────────
const IssueDrawer: React.FC<{ issue: Issue | null; onClose: () => void; onUpdateStatus: (id: number, s: IssueStatus, notes?: string, assignedTo?: string) => void; actorRole: string }> = ({ issue, onClose, onUpdateStatus, actorRole }) => {
    const [notes, setNotes] = useState('');
    const [assignedTo, setAssignedTo] = useState(issue?.assigned_to || '');
    const [users, setUsers] = useState<any[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    React.useEffect(() => {
        const farmerReported = issue?.reported_by_role === 'farmer'
            || (!issue?.reported_by_role && Boolean(issue?.farmer_id || issue?.farmer_name));
        const canManage = farmerReported
            ? ['agronomist', 'agronomist_advisor'].includes(actorRole)
            : actorRole === 'project_manager';
        if (canManage && issue?.id != null) {
            setLoadingUsers(true);
            apiGet('/api/users')
                .then((res: any) => {
                    const list = res?.users || res?.data || (Array.isArray(res) ? res : []);
                    setUsers(list.filter((u: any) => u.role !== 'farmer' && u.role !== 'super_admin'));
                })
                .catch((err: any) => { console.error('Failed to load users:', err); })
                .finally(() => setLoadingUsers(false));
        }
    }, [actorRole, issue?.id, issue?.assigned_to]);

    React.useEffect(() => {
        setNotes('');
        setAssignedTo(issue?.assigned_to || '');
    }, [issue?.id]);

    if (!issue) return null;
    const statusKeys = Object.keys(STATUS_FLOW) as IssueStatus[];
    const currentIdx = statusKeys.indexOf(issue.status);

    // Check if this is a farmer-reported issue
    const isFarmerIssue = issue.reported_by_role === 'farmer'
        || (!issue.reported_by_role && Boolean(issue.farmer_id || issue.farmer_name));
    const canManage = isFarmerIssue
        ? ['agronomist', 'agronomist_advisor'].includes(actorRole)
        : actorRole === 'project_manager';

    return (
        <div className="fixed inset-0 z-40 flex justify-end">
            <div className="fixed inset-0 bg-slate-950/35" onClick={onClose} />
            <div className="relative flex h-full w-full max-w-xl flex-col overflow-hidden bg-white shadow-2xl">
                {/* Header */}
                <div className="border-b border-slate-200 bg-white px-5 py-4">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-1.5 mb-2">
                                <Badge 
                                    label={CATEGORIES[issue.category]?.label || issue.category} 
                                    color={CATEGORIES[issue.category]?.color || 'text-gray-700'} 
                                    bg={CATEGORIES[issue.category]?.bg || 'bg-gray-100'} 
                                    icon={CATEGORIES[issue.category]?.icon}
                                    size="md"
                                />
                                <Badge 
                                    label={URGENCY[issue.urgency]?.label || issue.urgency} 
                                    color={URGENCY[issue.urgency]?.color || 'text-gray-700'} 
                                    bg="" 
                                    dot={URGENCY[issue.urgency]?.dot} 
                                    size="md"
                                />
                                <Badge 
                                    label={STATUS_FLOW[issue.status]?.label || issue.status} 
                                    color={STATUS_FLOW[issue.status]?.color || 'text-gray-700'} 
                                    bg={STATUS_FLOW[issue.status]?.bg || 'bg-gray-100'} 
                                    size="md"
                                />
                            </div>
                            <h2 className="text-lg font-bold leading-snug text-slate-900">
                                {issue.title || issue.description.substring(0, 60)}
                            </h2>
                        </div>
                        <button onClick={onClose} aria-label="Close issue details" className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors flex-shrink-0">
                            <XMarkIcon className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 space-y-4 overflow-y-auto bg-slate-50 p-4">
                    {/* Status Progress */}
                    <div>
                        <p className="mb-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">Status timeline</p>
                        <div className="flex items-center gap-1">
                            {statusKeys.filter(s => s !== 'closed').map((s, i) => (
                                <React.Fragment key={s}>
                                    <div className="flex-1 text-center">
                                        <div className={`mx-auto flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${i <= currentIdx ? 'bg-emerald-700 text-white' : 'bg-slate-200 text-slate-500'}`}>
                                            {i < currentIdx ? '✓' : i + 1}
                                        </div>
                                        <p className={`mt-1.5 text-[11px] font-medium ${i <= currentIdx ? 'text-emerald-800' : 'text-slate-400'}`}>{STATUS_FLOW[s].label}</p>
                                    </div>
                                    {i < statusKeys.length - 2 && <div className={`h-0.5 w-6 flex-shrink-0 ${i < currentIdx ? 'bg-emerald-600' : 'bg-slate-200'}`} />}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>

                    {/* Description */}
                    <div>
                        <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">Report details</p>
                        <p className="rounded-lg border border-slate-200 bg-white p-4 text-sm leading-relaxed text-slate-700">{issue.description}</p>
                    </div>

                    {typeof issue.report_metadata?.category_detail === 'string' && issue.report_metadata.category_detail.trim() && (
                        <div className="rounded-lg border border-slate-200 bg-white p-4">
                            <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">Additional field details</p>
                            <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-800">{issue.report_metadata.category_detail}</p>
                            {typeof issue.report_metadata.amount_requested === 'number' && (
                                <p className="mt-2 text-sm font-semibold text-gray-800">Amount requested: {issue.report_metadata.amount_requested.toLocaleString()} RWF</p>
                            )}
                        </div>
                    )}

                    {issue.audio_url && (
                        <div className="rounded-lg border border-slate-200 bg-white p-4">
                            <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">Voice evidence</p>
                            <audio controls preload="metadata" src={issue.audio_url} className="w-full" />
                        </div>
                    )}

                    {/* Photo Evidence */}
                    {(issue.photo_url || (issue.photo_urls && issue.photo_urls.length > 0)) && (
                        <div>
                            <p className="mb-2 flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                                <PhotoIcon className="w-3 h-3" /> Photo Evidence
                            </p>
                            <div className="flex gap-2 overflow-x-auto pb-1">
                                {issue.photo_url && (
                                    <a href={issue.photo_url} target="_blank" rel="noopener noreferrer" className="block flex-shrink-0">
                                        <img src={issue.photo_url} alt="Issue" className="h-24 w-24 object-cover rounded-xl border border-gray-200 hover:border-green-400 transition-colors" />
                                    </a>
                                )}
                                {(issue.photo_urls || []).filter(u => u !== issue.photo_url).map((url, idx) => (
                                    <a key={idx} href={url} target="_blank" rel="noopener noreferrer" className="block flex-shrink-0">
                                        <img src={url} alt={`Evidence ${idx + 2}`} className="h-24 w-24 object-cover rounded-xl border border-gray-200 hover:border-green-400 transition-colors" />
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* GPS Coordinates */}
                    {issue.latitude != null && issue.longitude != null && (
                        <div className="rounded-lg border border-slate-200 bg-white p-4">
                            <p className="mb-2 flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                                <MapPinIcon className="w-3 h-3" /> GPS Coordinates
                            </p>
                            <div className="flex items-center gap-3">
                                <span className="font-mono text-sm text-slate-700">
                                    {Number(issue.latitude).toFixed(6)}, {Number(issue.longitude).toFixed(6)}
                                </span>
                                <a href={`https://www.google.com/maps?q=${Number(issue.latitude)},${Number(issue.longitude)}`} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-emerald-800 hover:text-emerald-900 underline">
                                    Open in Maps
                                </a>
                            </div>
                        </div>
                    )}

                    {/* Reporter Info */}
                    {isFarmerIssue && (
                        <div className="rounded-lg border border-slate-200 bg-white p-4">
                            <p className="mb-3 flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                                <UserIcon className="w-3 h-3" /> Reported By Farmer
                            </p>
                            <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100">
                                    <span className="text-lg font-bold text-emerald-800">
                                        {(issue.farmer_name || issue.reported_by || 'F')[0].toUpperCase()}
                                    </span>
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-800 text-sm">{issue.farmer_name || issue.reported_by}</p>
                                    {issue.farmer_phone && (
                                        <p className="text-xs text-gray-500 flex items-center gap-1">
                                            <PhoneIcon className="w-3 h-3" /> {issue.farmer_phone}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Info Grid */}
                    <div className="grid grid-cols-2 gap-3">
                        {issue.tree_id && (
                            <div className="col-span-2 rounded-lg border border-slate-200 bg-white p-3">
                                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Affected tree</p>
                                <p className="mt-1 font-bold text-slate-900">
                                    <a href={`/traceability?tree=${issue.tree_id}`} className="hover:underline">{issue.tree_code || `Tree #${issue.tree_id}`}</a>
                                </p>
                                <p className="text-xs capitalize text-slate-500">{[issue.crop_type, issue.variety, issue.tree_health_status].filter(Boolean).join(' · ')}</p>
                            </div>
                        )}
                        {!isFarmerIssue && (
                            <div className="rounded-lg border border-slate-200 bg-white p-3">
                                <p className="text-[11px] font-medium text-slate-500">Reported by</p>
                                <p className="mt-0.5 text-sm font-semibold text-slate-800">{issue.reported_by}</p>
                                <p className="text-[11px] capitalize text-slate-400">{issue.reported_by_role?.replace('_', ' ')}</p>
                            </div>
                        )}
                        <div className="rounded-lg border border-slate-200 bg-white p-3">
                            <p className="text-[11px] font-medium text-slate-500">Location</p>
                            <p className="mt-0.5 text-sm font-semibold text-slate-800">{issue.location || issue.address || '—'}</p>
                        </div>
                        <div className="rounded-lg border border-slate-200 bg-white p-3">
                            <p className="text-[11px] font-medium text-slate-500">Assigned to</p>
                            {canManage && issue.status !== 'resolved' && issue.status !== 'closed' ? (
                                <select
                                    value={assignedTo}
                                    onChange={(e) => setAssignedTo(e.target.value)}
                                    className="mt-1 w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white"
                                >
                                    {loadingUsers
                                        ? <option value="" disabled>Loading field staff...</option>
                                        : <option value="">Unassigned</option>}
                                    {users.map((u: any) => (
                                        <option key={u.id} value={u.full_name || u.name || u.email}>
                                            {u.full_name || u.name || u.email} ({u.role})
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <p className="text-sm font-semibold text-gray-800 mt-0.5">{issue.assigned_to || 'Unassigned'}</p>
                            )}
                        </div>
                    </div>

                    {/* Resolution Notes */}
                    {issue.resolution_notes && (
                        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                            <p className="mb-1 text-[11px] font-bold text-emerald-900">Resolution notes</p>
                            <p className="text-sm text-emerald-900">{issue.resolution_notes}</p>
                        </div>
                    )}

                    {/* Field staff triage */}
                    {canManage && issue.status !== 'resolved' && issue.status !== 'closed' && (
                        <div className="space-y-3 rounded-lg border border-emerald-200 bg-white p-4">
                            <p className="text-sm font-bold text-emerald-800">Field Staff Response</p>
                                <textarea
                                value={notes} 
                                onChange={e => setNotes(e.target.value)} 
                                rows={2} 
                                placeholder="Add resolution notes..."
                                className="w-full resize-none rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                            <div className="flex gap-2">
                                {(issue.status === 'submitted' || issue.status === 'assigned') && (
                                    <button 
                                        onClick={() => onUpdateStatus(issue.id, 'assigned', notes, assignedTo || undefined)}
                                        className="flex-1 py-2.5 text-sm font-bold bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 shadow-sm transition-colors"
                                    >
                                        {assignedTo ? `Assign to ${assignedTo.split(' ')[0]} →` : 'Assign →'}
                                    </button>
                                )}
                                {issue.status === 'assigned' && (
                                    <button 
                                        onClick={() => onUpdateStatus(issue.id, 'in_progress', notes)}
                                        className="flex-1 py-2.5 text-sm font-bold bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 shadow-sm transition-colors"
                                    >
                                        Start Progress →
                                    </button>
                                )}
                                {issue.status === 'in_progress' && (
                                    <button 
                                        onClick={() => onUpdateStatus(issue.id, 'resolved', notes)}
                                        className="flex-1 py-2.5 text-sm font-bold bg-green-600 text-white rounded-lg hover:bg-green-700 shadow-sm transition-colors"
                                    >
                                        Mark Resolved ✓
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                    {!canManage && issue.status !== 'resolved' && issue.status !== 'closed' && (
                        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                            {isFarmerIssue
                                ? 'This farmer report is waiting for an agronomist.'
                                : 'This staff report is waiting for a project manager.'}
                        </div>
                    )}
                </div>

                <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between text-[11px] text-gray-400">
                    <span>Reported {new Date(issue.created_at).toLocaleDateString()}</span>
                    <span>Updated {new Date(issue.updated_at).toLocaleDateString()}</span>
                </div>
            </div>
        </div>
    );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const IssueManagementPage: React.FC = () => {
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedStatus = searchParams.get('status');
    const issueStatuses = ['submitted', 'assigned', 'in_progress', 'resolved', 'closed'];
    const initialStatus = requestedStatus === 'open' || issueStatuses.includes(requestedStatus || '')
        ? requestedStatus as 'open' | IssueStatus
        : 'all';
    const actorRole = user?.role || '';
    const canReportIssues = ['agronomist', 'agronomist_advisor', 'field_facilitator'].includes(actorRole);
    const [issues, setIssues] = useState<Issue[]>([]);
    const [trees, setTrees] = useState<TreeOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [filterCategory, setFilterCategory] = useState<'all' | IssueCategory>('all');
    const [filterUrgency, setFilterUrgency] = useState<'all' | IssueUrgency>('all');
    const [filterStatus, setFilterStatus] = useState<'all' | 'open' | IssueStatus>(initialStatus);
    const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
    const [showReportModal, setShowReportModal] = useState(false);

    const fetchIssues = async () => {
        try {
            setLoading(true);
            setLoadError(null);
            // Fetch from farmer-issues endpoint which includes farmer names
            const data = await apiGet<any>('/api/farmer-issues');
            setIssues((data?.issues || data || []).map(normalizeIssue));
        } catch (primaryError: any) {
            // Try the general issues endpoint as fallback
            try {
                const data = await apiGet<any>('/api/issues');
                setIssues((data?.issues || data || []).map(normalizeIssue));
            } catch (fallbackError: any) {
                setIssues([]);
                setLoadError(fallbackError?.message || primaryError?.message || 'Issues could not be loaded.');
            }
        } finally { setLoading(false); }
    };

    const fetchTrees = async () => {
        try {
            const data = await apiGet<any>('/api/maps/trees');
            setTrees(Array.isArray(data) ? data : []);
        } catch {
            setTrees([]);
        }
    };

    useEffect(() => {
        fetchIssues();
        fetchTrees();
    }, []);

    useEffect(() => {
        setFilterStatus(initialStatus);
    }, [initialStatus]);

    const changeStatusFilter = (next: 'all' | 'open' | IssueStatus) => {
        setFilterStatus(next);
        const params = new URLSearchParams(searchParams);
        if (next === 'all') params.delete('status');
        else params.set('status', next);
        setSearchParams(params, { replace: true });
    };

    const handleSubmitIssue = async (formData: any) => {
        const payload = {
            ...formData,
            reported_by: user?.full_name || 'Unknown',
            reported_by_role: user?.role || 'field_facilitator'
        };
        await apiPost('/api/farmer-issues', payload);
        await fetchIssues();
    };

    const handleUpdateStatus = async (id: number, status: IssueStatus, notes?: string, assignedTo?: string) => {
        try {
            const payload: any = { status };
            if (notes) payload.resolution_notes = notes;
            if (assignedTo) payload.assigned_to = assignedTo;
            const result = await apiPatch<any>(`/api/farmer-issues/${id}`, payload);
            // Update selected issue in-place so the drawer reflects the change immediately
            if (result && (result.issue || result.id)) {
                const updated = result.issue || result;
                setSelectedIssue(prev => prev?.id === id ? { ...prev, ...updated } : prev);
                setIssues(p => p.map(i => i.id === id ? { ...i, ...updated } : i));
            } else {
                // Fallback: optimistically update
                setSelectedIssue(prev => prev?.id === id ? { ...prev, status, assigned_to: assignedTo || prev.assigned_to, resolution_notes: notes || prev.resolution_notes } : prev);
                setIssues(p => p.map(i => i.id === id ? { ...i, status, assigned_to: assignedTo || i.assigned_to, resolution_notes: notes || i.resolution_notes } : i));
            }
        } catch (err: any) {
            setLoadError(err?.message || 'The issue update failed. No changes were saved.');
        }
    };

    const filtered = issues.filter(i => {
        if (filterCategory !== 'all' && i.category !== filterCategory) return false;
        if (filterUrgency !== 'all' && i.urgency !== filterUrgency) return false;
        if (filterStatus === 'open' && ['resolved', 'closed', 'rejected'].includes(i.source_status || i.status)) return false;
        if (filterStatus !== 'all' && filterStatus !== 'open' && i.status !== filterStatus) return false;
        const searchLower = search.toLowerCase();
        const titleMatch = i.title?.toLowerCase().includes(searchLower) ?? false;
        const descMatch = i.description?.toLowerCase().includes(searchLower) ?? false;
        const reporterMatch = i.reported_by?.toLowerCase().includes(searchLower) ?? false;
        const farmerMatch = i.farmer_name?.toLowerCase().includes(searchLower) ?? false;
        if (search && !titleMatch && !descMatch && !reporterMatch && !farmerMatch) return false;
        return true;
    });

    // Stats
    const critical = issues.filter(i => i.urgency === 'critical' && i.status !== 'resolved' && i.status !== 'closed').length;
    const open = issues.filter(i => i.status !== 'resolved' && i.status !== 'closed').length;
    const resolved = issues.filter(i => i.status === 'resolved').length;
    const inProgress = issues.filter(i => i.status === 'in_progress').length;
    
    // Farmer-reported issues count
    return (
        <div className="min-h-screen bg-slate-50 p-4 md:p-6">
            <div className="mx-auto max-w-6xl space-y-5">

                {/* ── HEADER ── */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                        <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
                            <span className="rounded-lg bg-emerald-100 p-2"><ExclamationTriangleIcon className="h-6 w-6 text-emerald-800" /></span>
                            Issue Management
                        </h1>
                        <p className="ml-12 mt-1 text-sm text-slate-500">{canReportIssues ? 'Report field challenges' : 'Review field reports and follow their progress'}</p>
                    </div>
                    <div className="flex gap-2">
                        <button 
                            onClick={() => fetchIssues()} 
                            className="flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50"
                        >
                            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
                        </button>
                        {canReportIssues && <button
                            onClick={() => setShowReportModal(true)}
                            className="flex items-center gap-1.5 rounded-lg bg-emerald-800 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-900"
                        >
                            <PlusIcon className="w-4 h-4" /> Report Issue
                        </button>}
                    </div>
                </div>

                {/* ── STATS ── */}
                <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
                    <StatCard 
                        title="Open Issues" 
                        value={open} 
                        subtitle="Needs attention" 
                        icon={<ExclamationTriangleIcon className="w-5 h-5 text-orange-600" />} 
                        accent="bg-orange-50" 
                    />
                    <StatCard 
                        title="Critical" 
                        value={critical} 
                        subtitle="Immediate action" 
                        icon={<BellAlertIcon className="w-5 h-5 text-red-600" />} 
                        accent="bg-red-50" 
                    />
                    <StatCard 
                        title="In Progress" 
                        value={inProgress} 
                        subtitle="Being resolved" 
                        icon={<ClockIcon className="w-5 h-5 text-yellow-600" />} 
                        accent="bg-yellow-50" 
                    />
                    <StatCard 
                        title="Resolved" 
                        value={resolved} 
                        subtitle="Successfully closed" 
                        icon={<CheckCircleIcon className="w-5 h-5 text-green-600" />} 
                        accent="bg-green-50" 
                    />
                </div>

                {loadError && <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"><span>{loadError}</span><button onClick={() => fetchIssues()} className="font-semibold underline">Retry</button></div>}

                {/* ── FILTERS ── */}
                <div className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-white p-3 md:flex-row">
                    <div className="relative flex-1">
                        <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input 
                            value={search} 
                            onChange={e => setSearch(e.target.value)} 
                            placeholder="Search issues..."
                            className="w-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-700/20"
                        />
                    </div>
                    <select 
                        value={filterCategory} 
                        onChange={e => setFilterCategory(e.target.value as any)}
                        className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-700/20"
                    >
                        <option value="all">All Categories</option>
                        {(Object.entries(CATEGORIES) as [IssueCategory, any][]).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                    </select>
                    <select 
                        value={filterUrgency} 
                        onChange={e => setFilterUrgency(e.target.value as any)}
                        className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-700/20"
                    >
                        <option value="all">All Urgencies</option>
                        {(Object.entries(URGENCY) as [IssueUrgency, any][]).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                    <select 
                        value={filterStatus} 
                        onChange={e => changeStatusFilter(e.target.value as 'all' | 'open' | IssueStatus)}
                        className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-700/20"
                    >
                        <option value="all">All Statuses</option>
                        <option value="open">Open issues</option>
                        {(Object.entries(STATUS_FLOW) as [IssueStatus, any][]).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                </div>

                {/* ── ISSUE LIST ── */}
                <div className="space-y-2">
                    {loading && (
                        <div className="flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-8 text-sm text-slate-500">
                            <ArrowPathIcon className="h-4 w-4 animate-spin" /> Loading issue reports...
                        </div>
                    )}
                    {filtered.length === 0 && !loading && !loadError && (
                        <div className="rounded-lg border border-slate-200 bg-white p-12 text-center">
                            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                                <ExclamationTriangleIcon className="w-8 h-8 text-gray-300" />
                            </div>
                            <p className="text-gray-600 font-semibold">No issues found</p>
                            <p className="text-gray-400 text-sm mt-1">Try adjusting your search or filters</p>
                        </div>
                    )}

                    {filtered.map(issue => {
                        const isFarmerIssue = issue.reported_by_role === 'farmer' || issue.farmer_name;
                        return (
                            <div 
                                key={issue.id}
                                onClick={() => setSelectedIssue(issue)}
                                className={`group cursor-pointer rounded-lg border bg-white transition-colors hover:border-emerald-400 ${issue.urgency === 'critical' ? 'border-red-200' : 'border-slate-200'}`}
                            >
                                <div className="p-4 flex gap-3 items-stretch">
                                    {/* Urgency Color Bar */}
                                    <div className={`w-1 rounded-full flex-shrink-0 ${URGENCY[issue.urgency]?.dot || 'bg-gray-300'}`} />

                                    {/* Content */}
                                    <div className="flex-1 min-w-0 space-y-2">
                                        {/* Badges Row */}
                                        <div className="flex flex-wrap items-center gap-1.5">
                                            <Badge 
                                                label={CATEGORIES[issue.category]?.label || issue.category} 
                                                color={CATEGORIES[issue.category]?.color || 'text-gray-700'} 
                                                bg={CATEGORIES[issue.category]?.bg || 'bg-gray-100'} 
                                                icon={CATEGORIES[issue.category]?.icon}
                                            />
                                            <Badge 
                                                label={URGENCY[issue.urgency]?.label || issue.urgency} 
                                                color={URGENCY[issue.urgency]?.color || 'text-gray-700'} 
                                                bg={issue.urgency === 'critical' ? 'bg-red-50 border border-red-200' : 'bg-gray-50'} 
                                                dot={URGENCY[issue.urgency]?.dot || 'bg-gray-500'} 
                                            />
                                            <Badge 
                                                label={STATUS_FLOW[issue.status]?.label || issue.status} 
                                                color={STATUS_FLOW[issue.status]?.color || 'text-gray-700'} 
                                                bg={STATUS_FLOW[issue.status]?.bg || 'bg-gray-100'} 
                                            />
                                            {isFarmerIssue && (
                                                <Badge label="Farmer Report" color="text-green-700" bg="bg-green-50 border border-green-200" />
                                            )}
                                            {issue.tree_id && (
                                                <Badge
                                                    label={issue.tree_code || `Tree #${issue.tree_id}`}
                                                    color="text-red-700"
                                                    bg="bg-red-50 border border-red-200"
                                                />
                                            )}
                                        </div>

                                        {/* Title & Description */}
                                        <h3 className="font-bold text-gray-900 text-[15px] leading-snug group-hover:text-green-700 transition-colors line-clamp-1">
                                            {issue.title || issue.description.substring(0, 80)}
                                        </h3>
                                        <p className="text-sm text-gray-500 line-clamp-2 leading-relaxed">{issue.description}</p>

                                        {/* Meta Row */}
                                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400 pt-1">
                                            <span className="flex items-center gap-1">
                                                <UserCircleIcon className="w-3.5 h-3.5 flex-shrink-0" />
                                                <span className="truncate max-w-[120px]">{issue.farmer_name || issue.reported_by || 'Unknown'}</span>
                                            </span>
                                            {(issue.location || issue.address) && (
                                                <span className="flex items-center gap-1">
                                                    <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" />
                                                    <span className="truncate max-w-[140px]">{issue.location || issue.address}</span>
                                                </span>
                                            )}
                                            <span>{new Date(issue.created_at).toLocaleDateString()}</span>
                                            {issue.assigned_to && (
                                                <span className="flex items-center gap-1 text-blue-600 font-medium">
                                                    <ArrowUpCircleIcon className="w-3.5 h-3.5 flex-shrink-0" />
                                                    {issue.assigned_to.split(' ')[0]}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Right Action Indicator */}
            <div className="flex-shrink-0 self-center opacity-60 group-hover:opacity-100 transition-opacity">
                                        {issue.photo_url && (
                                            <img 
                                                src={issue.photo_url} 
                                                alt="" 
                                                className="w-10 h-10 rounded-lg object-cover border border-gray-200 mb-2"
                                            />
                                        )}
                                        <ArrowRightIcon className="w-5 h-5 text-green-500 ml-auto" />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

            </div>

            {/* ── MODALS & DRAWERS ── */}
            {showReportModal && <ReportIssueModal onClose={() => setShowReportModal(false)} onSubmit={handleSubmitIssue} trees={trees} />}
            {selectedIssue && (
                <IssueDrawer
                    issue={selectedIssue}
                    onClose={() => setSelectedIssue(null)}
                    onUpdateStatus={handleUpdateStatus}
                    actorRole={actorRole}
                />
            )}
        </div>
    );
};

export default IssueManagementPage;
 
